import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  GoogleSignInButton,
  PasswordSignInForm,
  useSession,
} from '@/shared/auth';
import { useBrand } from '@/shared/brand/useBrand';
import { PageHeader } from '@/shared/components/PageHeader';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { LegalFooter } from '@/shared/legal';
import { useI18n } from '@/shared/i18n/useT';

/**
 * `/signin` on numtable.com — for members of an organization.
 *
 * Deliberately a separate screen from `client/SignIn.tsx` even though both
 * currently render the same two controls: the audiences differ (staff vs end
 * clients), the landing paths differ, and CLAUDE.md forbids sharing UI between
 * the two shells. The parts that must not drift — the auth calls themselves —
 * are shared through `@/shared/auth`.
 */
export function SignInPage() {
  const { t } = useI18n();
  const brand = useBrand();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { status } = useSession();

  const title = t('auth.signIn.title');
  useEffect(() => {
    document.title = `${title} · ${brand.name}`;
  }, [title, brand.name]);

  // Same-origin path only. An absolute URL here would make this an open redirect.
  const next = params.get('next');
  const safeNext = next?.startsWith('/') && !next.startsWith('//') ? next : null;
  const landing = safeNext ?? '/';

  // Someone who is already signed in has no business on this screen — and
  // arrives here by back button more often than by choice.
  useEffect(() => {
    if (status === 'signedIn') navigate(landing, { replace: true });
  }, [status, landing, navigate]);

  return (
    <div className="py-6 sm:py-8">
      <PageHeader
        title={title}
        description={t('auth.signIn.description')}
        actions={<LocaleSwitcher compact />}
      />

      <div className="mx-auto mt-6 w-full max-w-sm">
        <GoogleSignInButton {...(safeNext === null ? {} : { next: safeNext })} />

        <div className="mt-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wide text-muted">
            {t('auth.password.or')}
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <PasswordSignInForm onSignedIn={() => navigate(landing, { replace: true })} />
      </div>

      <LegalFooter />
    </div>
  );
}
